import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { fetchSignupStatus, signIn, signUp } from '../api/auth'
import { ApiError } from '../api/client'
import { BetaBadge } from '../components/BetaBadge'
import { LanguageToggle } from '../components/LanguageToggle'
import { ThemeToggle } from '../components/ThemeToggle'
import { Button } from '../components/ui/Button'
import { Field, Input } from '../components/ui/Field'
import { useT } from '../i18n/context'
import { useAuthStore } from '../stores/auth'
import { toast } from '../stores/toast'

type Mode = 'signin' | 'signup'

export function LoginPage() {
    const t = useT()
    const status = useAuthStore(s => s.status)
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const inviteToken = params.get('inviteToken') ?? ''
    // Landing-CTA „Kostenlos starten“ linkt auf ?mode=register
    const [mode, setMode] = useState<Mode>(
        inviteToken || params.get('mode') === 'register' ? 'signup' : 'signin',
    )
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [name, setName] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null)

    useEffect(() => {
        // Öffentliche Info: ist Registrierung erlaubt? (GET, idempotent)
        fetchSignupStatus()
            .then(res => setSignupEnabled(res.enabled))
            .catch(() => setSignupEnabled(null))
    }, [])

    if (status === 'authenticated') return <Navigate to='/track' replace />

    const signupDisabled = signupEnabled === false

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setBusy(true)
        try {
            if (mode === 'signin') {
                await signIn(email, password)
            } else {
                const res = await signUp(
                    email,
                    password,
                    name,
                    inviteToken || undefined,
                )
                if (inviteToken && res.inviteAccepted === false) {
                    toast.error(t('login.inviteMismatch'))
                }
            }
            navigate('/track', { replace: true })
        } catch (err) {
            setError(
                err instanceof ApiError
                    ? err.message
                    : t('login.unexpected'),
            )
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className='flex min-h-screen items-center justify-center bg-gradient-to-b from-indigo-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 px-4'>
            <div className='absolute top-4 right-4 flex items-center gap-2'>
                <ThemeToggle className='bg-white/60 dark:bg-slate-800/60' />
                <LanguageToggle />
            </div>
            <div className='w-full max-w-sm'>
                <div className='mb-6 flex items-center justify-center gap-2 text-2xl font-bold tracking-tight text-indigo-800 dark:text-indigo-300'>
                    <span className='flex h-10 w-10 items-center justify-center rounded-[10px] bg-indigo-600 dark:bg-indigo-500 text-white'>
                        <svg
                            className='h-6 w-6'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2.4}
                            strokeLinecap='round'
                            aria-hidden='true'
                        >
                            <circle cx='12' cy='12' r='9' />
                            <path d='M12 7v5l3.5 2' />
                        </svg>
                    </span>
                    Optimus Time
                    <BetaBadge />
                </div>
                <div className='rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm'>
                    <div className='mb-4 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1'>
                        {(
                            [
                                ['signin', t('login.signin')],
                                ['signup', t('login.signup')],
                            ] as const
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type='button'
                                disabled={value === 'signup' && signupDisabled}
                                onClick={() => {
                                    setMode(value)
                                    setError(null)
                                }}
                                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                    mode === value
                                        ? 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 disabled:hover:text-slate-400'
                                } ${value === 'signup' && signupDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {signupDisabled && (
                        <p className='mb-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-xs text-slate-500 dark:text-slate-400'>
                            {t('login.signupDisabled')}
                            {inviteToken
                                ? t('login.signupDisabledInvite')
                                : ''}
                        </p>
                    )}
                    {inviteToken && !signupDisabled && mode === 'signup' && (
                        <p className='mb-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 px-3 py-2 text-xs text-indigo-800 dark:text-indigo-300'>
                            {t('login.inviteHint')}
                        </p>
                    )}
                    {mode === 'signup' && (
                        <p className='mb-3 rounded-lg border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200'>
                            {t('login.betaHint')}
                        </p>
                    )}

                    <form onSubmit={submit} className='space-y-3'>
                        {mode === 'signup' && (
                            <Field label={t('login.name')}>
                                <Input
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    maxLength={128}
                                    autoComplete='name'
                                />
                            </Field>
                        )}
                        <Field label={t('login.email')}>
                            <Input
                                type='email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                autoComplete='email'
                            />
                        </Field>
                        <Field
                            label={t('login.password')}
                            className={mode === 'signup' ? '' : 'pb-1'}
                        >
                            <Input
                                type='password'
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                minLength={8}
                                maxLength={128}
                                autoComplete={
                                    mode === 'signin'
                                        ? 'current-password'
                                        : 'new-password'
                                }
                            />
                        </Field>
                        {mode === 'signup' && (
                            <p className='text-xs text-slate-500 dark:text-slate-400'>
                                {t('login.passwordHint')}
                            </p>
                        )}
                        {mode === 'signin' && (
                            <p className='text-right'>
                                <Link
                                    to='/resetPassword'
                                    className='text-xs font-medium text-indigo-700 dark:text-indigo-400 hover:underline'
                                >
                                    {t('login.forgot')}
                                </Link>
                            </p>
                        )}
                        {error && (
                            <p
                                role='alert'
                                className='rounded-lg bg-red-50 dark:bg-red-950/60 px-3 py-2 text-sm text-red-700 dark:text-red-300'
                            >
                                {error}
                            </p>
                        )}
                        <Button type='submit' loading={busy} className='w-full'>
                            {mode === 'signin'
                                ? t('login.signin')
                                : t('login.createAccount')}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    )
}
